import { useEffect, useMemo } from "react";
import ReactFlow, {
  type NodeChange,
  Background,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  NodeMouseHandler,
  Node,
  Position,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { Committee } from "@/types/governance";
import { useState } from "react";

interface GovernanceGraphViewProps {
  committees: Committee[];
  selectedCommitteeId?: string | null;
  onCommitteeSelect?: (committeeId: string) => void;
}

export function GovernanceGraphView({
  committees,
  selectedCommitteeId = null,
  onCommitteeSelect,
}: GovernanceGraphViewProps) {
  const levelColorMap: Record<number, { border: string; background: string; chip: string }> = {
    1: {
      border: "hsl(265 85% 45%)",
      background: "hsl(265 100% 98%)",
      chip: "bg-violet-600",
    },
    2: {
      border: "hsl(217 91% 45%)",
      background: "hsl(216 100% 97%)",
      chip: "bg-blue-600",
    },
    3: {
      border: "hsl(160 84% 35%)",
      background: "hsl(151 81% 96%)",
      chip: "bg-emerald-600",
    },
    4: {
      border: "hsl(26 95% 42%)",
      background: "hsl(48 96% 94%)",
      chip: "bg-amber-600",
    },
    5: {
      border: "hsl(346 77% 45%)",
      background: "hsl(355 100% 97%)",
      chip: "bg-rose-600",
    },
  };

  const { nodes: computedNodes, edges } = useMemo(() => {
    const nodesByLevel = committees.reduce<Record<number, Committee[]>>((acc, committee) => {
      if (!acc[committee.level]) acc[committee.level] = [];
      acc[committee.level].push(committee);
      return acc;
    }, {});

    const orderedLevels = Object.keys(nodesByLevel)
      .map(Number)
      .sort((a, b) => a - b);

    const outNodes: Node[] = [];
    const outEdges: Edge[] = [];
    const xSpacing = 320;
    const ySpacing = 180;

    orderedLevels.forEach((level) => {
      const row = nodesByLevel[level];
      const rowOffset = (row.length - 1) / 2;
      row.forEach((committee, idx) => {
        const palette = levelColorMap[committee.level] ?? {
          border: "hsl(var(--border))",
          background: "hsl(var(--card))",
          chip: "bg-slate-600",
        };
        outNodes.push({
          id: committee.id,
          type: "default",
          // Top-to-bottom layout with each row centered around X = 0.
          position: { x: (idx - rowOffset) * xSpacing, y: level * ySpacing },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          data: {
            label: `${committee.name} (L${committee.level})`,
          },
          style: {
            width: 260,
            borderRadius: 10,
            border:
              committee.id === selectedCommitteeId
                ? `2px solid ${palette.border}`
                : `1px solid ${palette.border}`,
            background: palette.background,
            fontSize: 12,
            boxShadow:
              committee.id === selectedCommitteeId
                ? `0 0 0 3px color-mix(in oklab, ${palette.border} 20%, transparent)`
                : "none",
          },
        });

        if (committee.parentId) {
          outEdges.push({
            id: `${committee.parentId}-${committee.id}`,
            source: committee.parentId,
            target: committee.id,
            type: "smoothstep",
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
          });
        }
      });
    });

    return { nodes: outNodes, edges: outEdges };
  }, [committees, selectedCommitteeId]);
  const [nodes, setNodes] = useState<Node[]>(computedNodes);

  useEffect(() => {
    setNodes((prev) => {
      const prevPositionMap = new Map(prev.map((node) => [node.id, node.position]));
      return computedNodes.map((node) => {
        const draggedPosition = prevPositionMap.get(node.id);
        return draggedPosition ? { ...node, position: draggedPosition } : node;
      });
    });
  }, [computedNodes]);

  const onNodesChange = (changes: NodeChange[]) => {
    setNodes((prev) => applyNodeChanges(changes, prev));
  };

  const handleNodeClick: NodeMouseHandler = (_, node) => {
    onCommitteeSelect?.(node.id);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Level Key:</span>
        {[1, 2, 3, 4, 5].map((level) => {
          const palette = levelColorMap[level];
          return (
            <div key={level} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1">
              <span className={`h-2.5 w-2.5 rounded-full ${palette.chip}`} />
              <span>L{level}</span>
            </div>
          );
        })}
      </div>
      <div className="h-[620px] w-full rounded-lg border">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} fitView onNodeClick={handleNodeClick}>
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
}
